import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Container, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import useAxios from '../../services/AxiosConfig';
import styles from '../ManagementPages.module.css';
import { TrashIcon, AddQuestionIcon } from '../../components/icons/Icons';
import { showAlert } from '../../components/PerinHealthAlertSlice';

const getApiErrorMessage = (error, fallbackMessage) => {
    const message = error?.response?.data?.message;
    if (typeof message === 'string' && message.trim()) {
        return message;
    }
    if (Array.isArray(message) && message.length > 0) {
        return message.join(', ');
    }
    return fallbackMessage;
};

const AddHub = () => {

    const groupData = useSelector((state) => state.userInfo.groups);
    const uniqueGroups = useSelector((state) => state.userInfo.uniqueGroups);
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const navigate = useNavigate();

    const hubSlice = useSelector((state) => state.hubManagementSlice);
    const editHubData = hubSlice;
    const updatingHub = hubSlice.updating;

    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const [selectedOrg, setSelectedOrg] = useState(0);
    const [selectedGroup, setSelectedGroup] = useState(uniqueGroups.length === 1 ? uniqueGroups[0].id : 0);
    const [quantity, setQuantity] = useState(0);
    const [quantityInput, setQuantityInput] = useState("0");

    const [hubEntries, setHubEntries] = useState([]);

    const [assignedSerialNumbers, setAssignedSerialNumbers] = useState([]);
    // Number of hubs that already exist for this group
    const [existingHubCount, setExistingHubCount] = useState(0);
    // Group ids that already have hubs, populated by the hub management page
    // when navigating here. Selecting one while adding is not allowed (they
    // should be updated instead).
    const groupsWithHubs = hubSlice.groupsWithHubs || [];

    const groupAlreadyHasHubs = !updatingHub
        && selectedGroup !== 0
        && selectedGroup !== ""
        && groupsWithHubs.includes(Number(selectedGroup));

    useEffect(() => {
        if (!updatingHub) {
            return;
        }

        const hubs = editHubData.hubs || [];
        const startingQuantity = editHubData.quantity || hubs.length;
        setSelectedOrg(editHubData.organizationId || 0);
        setSelectedGroup(editHubData.groupId || 0);
        setQuantity(startingQuantity);
        setQuantityInput(String(startingQuantity));
        setExistingHubCount(hubs.length);
        setHubEntries(hubs.map((hub) => ({
            serialNumber: hub.serialNumber || '',
            macAddress: hub.macAddress || ''
        })));
        setAssignedSerialNumbers(
            Array.from(new Set(hubs.filter((item) => item.isAssigned).map((item) => item.serialNumber)))
        );
    }, [updatingHub, editHubData]);

    const [duplicateSerialNumbers, setDuplicateSerialNumbers] = useState(new Set());
    const [duplicateMacAddresses, setDuplicateMacAddresses] = useState(new Set());

    const handleQuantityInputChange = (e) => {
        const value = e.target.value;
        if (value !== "" && !/^\d*$/.test(value)) {
            return;
        }
        setQuantityInput(value);
    };

    // Commit the typed quantity (on blur) so the serial number rows only
    // update once the user is done editing the quantity field.
    const commitQuantity = () => {
        if (quantityInput === "") {
            setQuantity("");
            return;
        }
        let numValue = Number(quantityInput);
        if (Number.isNaN(numValue)) {
            return;
        }
        if (updatingHub && numValue < existingHubCount) {
            numValue = existingHubCount;
            setErrorMessage(`You can only add hubs. Quantity cannot be less than ${existingHubCount}.`);
        }
        setQuantity(numValue);
        setQuantityInput(String(numValue));
    };

    const handleSerialNumberChange = (index, value) => {
        const updatedEntries = [...hubEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            serialNumber: value
        };
        setHubEntries(updatedEntries);

        if (duplicateSerialNumbers.has(index)) {
            const newDuplicates = new Set(duplicateSerialNumbers);
            newDuplicates.delete(index);
            setDuplicateSerialNumbers(newDuplicates);
        }
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleMacAddressChange = (index, value) => {
        const updatedEntries = [...hubEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            macAddress: value
        };
        setHubEntries(updatedEntries);

        if (duplicateMacAddresses.has(index)) {
            const newDuplicates = new Set(duplicateMacAddresses);
            newDuplicates.delete(index);
            setDuplicateMacAddresses(newDuplicates);
        }
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleSerialDelete = (index) => {
        const updatedEntries = hubEntries.filter((_, i) => i !== index);
        const newCount = Number(quantity) - 1;
        setHubEntries(updatedEntries);
        setQuantity(newCount);
        setQuantityInput(String(newCount));
    };

    const addSerialNumber = () => {
        const newCount = Number(quantity) + 1;
        setHubEntries([...hubEntries, { serialNumber: '', macAddress: '' }]);
        setQuantity(newCount);
        setQuantityInput(String(newCount));
    };

    const findDuplicates = (entries) => {
        const serialNumberCounts = {};
        const macAddressCounts = {};
        const duplicateSerials = new Set();
        const duplicateMacs = new Set();

        entries.forEach((entry, index) => {
            if (entry.serialNumber.trim()) {
                serialNumberCounts[entry.serialNumber] = (serialNumberCounts[entry.serialNumber] || []).concat(index);
            }
            if (entry.macAddress.trim()) {
                macAddressCounts[entry.macAddress] = (macAddressCounts[entry.macAddress] || []).concat(index);
            }
        });

        Object.values(serialNumberCounts).forEach((indices) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateSerials.add(index));
            }
        });

        Object.values(macAddressCounts).forEach((indices) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateMacs.add(index));
            }
        });

        return { duplicateSerials, duplicateMacs };
    };

    const validateHub = () => {
        setDuplicateSerialNumbers(new Set());
        setDuplicateMacAddresses(new Set());

        if (formRef.current) {
            const formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(true);
            if (formIsInvalid) {
                setErrorMessage('Group and Quantity are required to proceed');
                return;
            }
        }

        if (selectedGroup === 0 || selectedGroup === "") {
            setErrorMessage('Group and Quantity are required to proceed');
            return;
        }

        if (quantity === "" || quantity <= 0) {
            setErrorMessage('Group and Quantity are required to proceed');
            return;
        }

        const entriesToCheck = hubEntries.slice(0, quantity);

        if (entriesToCheck.length !== quantity) {
            setErrorMessage('Please fill in all serial numbers and mac addresses.');
            return;
        }

        const allEntriesFilled = entriesToCheck.every((entry) =>
            entry.serialNumber.trim() !== "" && entry.macAddress.trim() !== ""
        );

        if (!allEntriesFilled) {
            setErrorMessage('Please fill in all serial numbers and mac addresses.');
            return;
        }

        const { duplicateSerials, duplicateMacs } = findDuplicates(entriesToCheck);
        if (duplicateSerials.size > 0 || duplicateMacs.size > 0) {
            setDuplicateSerialNumbers(duplicateSerials);
            setDuplicateMacAddresses(duplicateMacs);
            setErrorMessage('Please ensure all serial numbers and mac addresses are unique.');
            return;
        }

        setErrorMessage(null);
        if (updatingHub) {
            updateHub();
        } else {
            sendHub();
        }
    };

    const sendHub = async () => {
        const entriesToSend = hubEntries.slice(0, quantity);
        const hubInfo = {
            groupId: selectedGroup,
            serialNumbers: entriesToSend
        };
        try {
            const response = await httpService.request({
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                url: 'hub/group',
                data: JSON.stringify(hubInfo),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully added hubs`,
                        type: 'success'
                    })
                );
                navigate('/hub-management');
            }
        } catch (error) {
            console.log("error:" + error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: getApiErrorMessage(error, "Could not add hubs"),
                    type: 'danger',
                })
            );
        }
    };

    const updateHub = async () => {
        if (quantity === "" || quantity <= 0) {
            setErrorMessage('Group and Quantity are required to proceed');
            return;
        }
        const entriesToSend = hubEntries.slice(0, quantity);
        const info = {
            serialNumbers: entriesToSend
        };
        try {
            const response = await httpService.request({
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                url: `hub/group/${editHubData.groupId}`,
                data: JSON.stringify(info),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: "Hub updated successfully",
                        type: 'success'
                    })
                );
                navigate('/hub-management');
            }
        } catch (error) {
            console.log("error:" + error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: getApiErrorMessage(error, "Error updating hub"),
                    type: 'danger',
                })
            );
        }
    };

    return (
        <div className={styles.MainCard}>
            <div className={styles.ContentTitle}>{updatingHub ? "Update Hub" : "Add Hub"}</div>
            <div className={styles.HideScrollContainer}>
                <Form ref={formRef} noValidate validated={validated}>
                    {isAdmin &&
                        <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingSelect" label="Organization">
                                <Form.Select
                                    disabled={updatingHub}
                                    value={selectedOrg}
                                    style={{ width: "300px" }}
                                    onChange={(e) => setSelectedOrg(Number(e.target.value))}
                                    className={`${styles.BorderedComponent}`}
                                    required
                                >
                                    <option value={''}>Select an Organization</option>
                                    {groupData.map((org) => (
                                        <option key={org.organizationId} id={org.organizationId} value={org.organizationId}>{org.organizationName}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Please select an organization.
                                </Form.Control.Feedback>
                            </FloatingLabel>
                        </Form.Group>
                    }
                    <Container style={{ paddingLeft: "0px", marginLeft: "0" }}>
                        <Row className='device-row' gap={10} xs="auto">
                            <Col>
                                <Form.Group className="mb-3">
                                    <FloatingLabel controlId="hubGroup" label="Group">
                                        <Form.Select
                                            className={`${styles.BorderedComponent}`}
                                            onChange={(e) => setSelectedGroup(Number(e.target.value))}
                                            value={selectedGroup}
                                            required
                                            disabled={updatingHub}
                                            style={{ width: "300px" }}
                                        >
                                            <option value="">Select a group</option>
                                            {isAdmin ?
                                                groupData.filter(organization => organization.organizationId === selectedOrg)
                                                    .map((organization) => (
                                                        organization.groups.map((group) => (
                                                            <option key={group.groupId} value={group.groupId}>
                                                                {group.groupName}
                                                            </option>
                                                        ))
                                                    ))
                                                :
                                                uniqueGroups.map((group) => (
                                                    <option key={group.id} value={group.id}>{group.name}</option>
                                                ))
                                            }
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            Please select a group.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <FloatingLabel
                                        controlId="hubQuantity"
                                        label="Quantity*"
                                        className="mb-3"
                                    >
                                        <Form.Control
                                            placeholder="Quantity*"
                                            className={`${styles.BorderedComponent}`}
                                            style={errorMessage && quantity <= 0 ? { width: "130px", border: "1px solid #DC3545" } : { width: "130px" }}
                                            value={quantityInput}
                                            onChange={handleQuantityInputChange}
                                            onBlur={commitQuantity}
                                            maxLength={3}
                                            min={updatingHub ? existingHubCount : undefined}
                                            isInvalid={errorMessage && quantity <= 0}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please select a valid quantity.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                            </Col>
                        </Row>
                        <div>
                            {Array.from({ length: quantity }).map((_, index) => (
                                <div key={`hub_entry_${index}`} style={{ padding: "10px 0", justifyContent: "flex-start", display: "flex", alignItems: "center", gap: "15px" }}>
                                    <Form.Group key={`serial_${index}`}>
                                        <FloatingLabel controlId={`serialNumber${index}`} label={`Serial Number ${index + 1}`}>
                                            <Form.Control
                                                type="text"
                                                placeholder={`Serial Number ${index + 1}`}
                                                value={hubEntries[index]?.serialNumber || ''}
                                                onChange={(e) => handleSerialNumberChange(index, e.target.value)}
                                                required
                                                isInvalid={duplicateSerialNumbers.has(index)}
                                                style={duplicateSerialNumbers.has(index) ? { border: "2px solid #DC3545" } : {}}
                                            />
                                            {duplicateSerialNumbers.has(index) && (
                                                <Form.Control.Feedback type="invalid">
                                                    This serial number is duplicated.
                                                </Form.Control.Feedback>
                                            )}
                                        </FloatingLabel>
                                    </Form.Group>

                                    <Form.Group key={`mac_${index}`}>
                                        <FloatingLabel controlId={`macAddress${index}`} label={`Mac Address`}>
                                            <Form.Control
                                                type="text"
                                                required
                                                placeholder={`Mac Address`}
                                                value={hubEntries[index]?.macAddress || ''}
                                                onChange={(e) => handleMacAddressChange(index, e.target.value)}
                                                isInvalid={duplicateMacAddresses.has(index)}
                                                style={duplicateMacAddresses.has(index) ? { border: "2px solid #DC3545" } : {}}
                                            />
                                            {duplicateMacAddresses.has(index) && (
                                                <Form.Control.Feedback type="invalid">
                                                    This MAC address is duplicated.
                                                </Form.Control.Feedback>
                                            )}
                                        </FloatingLabel>
                                    </Form.Group>

                                    {!assignedSerialNumbers.includes(hubEntries[index]?.serialNumber) ?
                                        <div onClick={() => handleSerialDelete(index)}>
                                            <TrashIcon />
                                        </div>
                                        :
                                        <div style={{ color: "red", fontSize: "13px" }}>
                                            {"Active"}
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                        <Button variant="primary" style={{ marginBottom: "30px" }} className={styles.AddAnotherButton} onClick={addSerialNumber}>
                            <AddQuestionIcon /> Add Serial Number
                        </Button>
                    </Container>
                </Form>
            </div>
            <div className={styles.ButtonsFooterContainerWithError}>
                {(groupAlreadyHasHubs || errorMessage) &&
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {groupAlreadyHasHubs
                            ? "This group already has hubs. Please update the existing hubs instead."
                            : errorMessage}
                    </div>
                }
                <div style={{ display: "flex", columnGap: "10px" }}>
                    <Button variant="primary" className={styles.CreateButton} onClick={validateHub} disabled={groupAlreadyHasHubs}>
                        {updatingHub ? "Update" : "Add"}
                    </Button>
                    <Button variant="secondary" className={styles.CancelButton} onClick={() => navigate('/hub-management')}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddHub;
